import { Suspense } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { queryClient } from "./api/queryClient";
import { AuthProvider } from "./auth/AuthContext";
import { ProtectedRoute } from "./auth/ProtectedRoute";
import { AppShell } from "./components/layout/AppShell";
import { DevBanner } from "./components/DevBanner";
import { loadDashboardPage, loadHistoryPage, loadLoginPage, loadSessionDetailPage } from "./offline/offlinePages";
import { lazyWithReload } from "./utils/lazyWithReload";

const ChallengesPage    = lazyWithReload(() => import("./pages/ChallengesPage").then(m => ({ default: m.ChallengesPage })));
const CompleteGoogleSignupPage = lazyWithReload(() => import("./pages/CompleteGoogleSignupPage").then(m => ({ default: m.CompleteGoogleSignupPage })));
const ConfirmEmailChangePage = lazyWithReload(() => import("./pages/ConfirmEmailChangePage").then(m => ({ default: m.ConfirmEmailChangePage })));
const DashboardPage     = lazyWithReload(loadDashboardPage);
const HistoryPage       = lazyWithReload(loadHistoryPage);
const LandingPage       = lazyWithReload(() => import("./pages/LandingPage").then(m => ({ default: m.LandingPage })));
const LinkGoogleAccountPage = lazyWithReload(() => import("./pages/LinkGoogleAccountPage").then(m => ({ default: m.LinkGoogleAccountPage })));
const LoginPage         = lazyWithReload(loadLoginPage);
const PeoplePage        = lazyWithReload(() => import("./pages/PeoplePage").then(m => ({ default: m.PeoplePage })));
const ProfilePage       = lazyWithReload(() => import("./pages/ProfilePage").then(m => ({ default: m.ProfilePage })));
const RegisterPage      = lazyWithReload(() => import("./pages/RegisterPage").then(m => ({ default: m.RegisterPage })));
const SessionDetailPage = lazyWithReload(loadSessionDetailPage);
const StatsPage         = lazyWithReload(() => import("./pages/StatsPage").then(m => ({ default: m.StatsPage })));
const UserProfilePage   = lazyWithReload(() => import("./pages/UserProfilePage").then(m => ({ default: m.UserProfilePage })));
const UserStatsPage     = lazyWithReload(() => import("./pages/UserStatsPage").then(m => ({ default: m.UserStatsPage })));
const FeedbackPage       = lazyWithReload(() => import("./pages/FeedbackPage").then(m => ({ default: m.FeedbackPage })));
const ForgotPasswordPage = lazyWithReload(() => import("./pages/ForgotPasswordPage").then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage  = lazyWithReload(() => import("./pages/ResetPasswordPage").then(m => ({ default: m.ResetPasswordPage })));
const VerifyEmailPage    = lazyWithReload(() => import("./pages/VerifyEmailPage").then(m => ({ default: m.VerifyEmailPage })));
const PrivacyPage        = lazyWithReload(() => import("./pages/PrivacyPage").then(m => ({ default: m.PrivacyPage })));
const TermsPage          = lazyWithReload(() => import("./pages/TermsPage").then(m => ({ default: m.TermsPage })));
const SupportPage        = lazyWithReload(() => import("./pages/SupportPage").then(m => ({ default: m.SupportPage })));
const DeleteAccountPage  = lazyWithReload(() => import("./pages/DeleteAccountPage").then(m => ({ default: m.DeleteAccountPage })));

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <DevBanner />
          <Suspense fallback={<div className="min-h-dvh bg-bg-base" />}>
            <Routes>
              {/* Public */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/complete-google-signup" element={<CompleteGoogleSignupPage />} />
              <Route path="/link-google-account" element={<LinkGoogleAccountPage />} />
              <Route path="/verify" element={<VerifyEmailPage />} />
              <Route path="/confirm-email-change" element={<ConfirmEmailChangePage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="/delete-account" element={<DeleteAccountPage />} />

              {/* Protected — wrapped in AppShell for nav */}
              <Route
                path="/logbook"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <DashboardPage />
                    </AppShell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/stats"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <StatsPage />
                    </AppShell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/challenges"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <ChallengesPage />
                    </AppShell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sessions/:id"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <SessionDetailPage />
                    </AppShell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <ProfilePage />
                    </AppShell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/history"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <HistoryPage />
                    </AppShell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/feedback"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <FeedbackPage />
                    </AppShell>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/crew"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <PeoplePage />
                    </AppShell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/crew/:handle"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <UserProfilePage />
                    </AppShell>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/crew/:handle/stats"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <UserStatsPage />
                    </AppShell>
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
